import React, { useEffect, useState } from 'react';
import { ArrowRight, CheckCircle, Sparkles } from 'lucide-react';

const Hero = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const features = [
    'Custom Software & ERP Development',
    'Cloud Architecture & AWS Migration',
    'Full-Stack Web & Mobile Apps',
    '24/7 SLA Maintenance & IT Consulting'
  ];

  const scrollToContact = () => {
    window.location.hash = '#contact';
  };

  const scrollToPortfolio = () => {
    window.location.hash = '#portfolio';
  };

  return (
    <section id="home" className="relative bg-gradient-to-br from-white via-[#f0fdf9] to-[#ecfeff] pt-24 pb-20 overflow-hidden">
      {/* 21st.dev Ambient Glowing Background Orbs */}
      <div className="absolute -top-24 -left-24 w-96 h-96 bg-[#2af598]/15 rounded-full blur-3xl pointer-events-none animate-aura-pulse"></div>
      <div className="absolute top-1/3 -right-24 w-96 h-96 bg-[#08b3e5]/15 rounded-full blur-3xl pointer-events-none animate-aura-pulse" style={{ animationDelay: '2.5s' }}></div>
      <div className="absolute -bottom-20 left-1/3 w-80 h-80 bg-[#14c9cb]/10 rounded-full blur-3xl pointer-events-none"></div>

      {/* Subtle fine tech grid background overlay */}
      <div className="absolute inset-0 tech-grid-fine opacity-40 pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className={`text-center lg:text-left transform transition-all duration-1000 ${
            isVisible ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'
          }`}>
            {/* 21st.dev Sparkle Announcement Badge */}
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/90 backdrop-blur-md border border-[#14c9cb]/30 text-xs font-bold uppercase tracking-wider text-[#0d9488] shadow-sm mb-6 animate-fade-in">
              <Sparkles className="w-3.5 h-3.5 text-[#14c9cb] animate-sparkle" />
              <span>Enterprise IT & Custom Software Engineering</span>
              <span className="w-2 h-2 rounded-full bg-[#2af598] animate-ping"></span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-slate-900 leading-tight mb-6 tracking-tight">
              <span className="inline-block animate-fade-in-up">Transforming Businesses with</span>{' '}
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#10b981] via-[#14c9cb] to-[#08b3e5] block animate-fade-in-up animation-delay-300">
                Innovative IT Solutions
              </span>
            </h1>
            <p className="text-base sm:text-lg text-slate-600 mb-8 leading-relaxed animate-fade-in-up animation-delay-600 max-w-xl mx-auto lg:mx-0">
              We empower startups and enterprises with high-throughput custom software, 
              scalable cloud infrastructures, and end-to-end digital transformation pipelines.
            </p>
            
            {/* Features List */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 mb-8">
              {features.map((feature, index) => (
                <div 
                  key={index} 
                  className="flex items-center justify-center lg:justify-start transform transition-all duration-500 hover:scale-[1.02] animate-fade-in-up"
                  style={{ animationDelay: `${800 + index * 150}ms` }}
                >
                  <div className="w-5 h-5 rounded-full bg-gradient-to-r from-[#2af598] to-[#14c9cb] flex items-center justify-center mr-2.5 flex-shrink-0 shadow-xs">
                    <CheckCircle className="h-3.5 w-3.5 text-white stroke-[3]" />
                  </div>
                  <span className="text-slate-700 text-sm font-semibold">{feature}</span>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start animate-fade-in-up animation-delay-1200">
              <button 
                onClick={scrollToContact}
                className="bg-gradient-to-r from-[#10b981] via-[#14c9cb] to-[#08b3e5] text-white px-8 py-3.5 rounded-xl font-bold transition-all duration-300 flex items-center justify-center group transform hover:scale-[1.03] shadow-lg hover:shadow-[0_8px_30px_rgba(20,201,203,0.35)] active:scale-[0.98]"
              >
                Get Started Today
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1.5 transition-transform duration-300" />
              </button>
              <button 
                onClick={scrollToPortfolio}
                className="bg-white/90 backdrop-blur-sm border-2 border-[#14c9cb]/70 text-[#0f766e] px-8 py-3.5 rounded-xl font-bold hover:bg-gradient-to-r hover:from-[#10b981] hover:to-[#14c9cb] hover:text-white hover:border-transparent transition-all duration-300 transform hover:scale-[1.03] shadow-md hover:shadow-lg active:scale-[0.98]"
              >
                View Our Work
              </button>
            </div>
          </div>

          {/* Right Content - Stats Cards */}
          <div className={`grid grid-cols-2 gap-5 transform transition-all duration-1000 ${
            isVisible ? 'translate-x-0 opacity-100' : 'translate-x-10 opacity-0'
          }`}>
            {[
              { number: '500+', label: 'Projects Completed', delay: '200ms', badge: 'Verified' },
              { number: '150+', label: 'Happy Clients', delay: '400ms', badge: 'Global' },
              { number: '10+', label: 'Years Experience', delay: '600ms', badge: 'Excellence' },
              { number: '24/7', label: 'Support Available', delay: '800ms', badge: 'SLA Active' }
            ].map((stat, index) => (
              <div 
                key={index} 
                className="relative bg-white/90 backdrop-blur-md p-6 rounded-2xl shadow-lg hover:shadow-[0_12px_36px_rgba(20,201,203,0.2)] transition-all duration-500 border border-slate-200/80 hover:border-[#14c9cb] transform hover:scale-105 hover:-translate-y-1.5 animate-fade-in-up group cursor-pointer overflow-hidden"
                style={{ animationDelay: stat.delay }}
              >
                {/* Top Subtle Gradient Line */}
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#2af598] via-[#14c9cb] to-[#08b3e5] opacity-80 group-hover:h-1.5 transition-all"></div>
                
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[10px] font-extrabold uppercase tracking-wider text-[#0d9488] bg-[#f0fdf9] px-2 py-0.5 rounded-md border border-[#14c9cb]/20">
                    {stat.badge}
                  </span>
                  <Sparkles className="w-3.5 h-3.5 text-[#14c9cb] opacity-0 group-hover:opacity-100 transition-opacity animate-sparkle" />
                </div>

                <div className="text-3xl sm:text-4xl font-black mb-1.5 bg-clip-text text-transparent bg-gradient-to-r from-[#10b981] via-[#14c9cb] to-[#08b3e5]">
                  {stat.number}
                </div>
                <div className="text-slate-600 text-xs sm:text-sm font-medium group-hover:text-slate-900 transition-colors">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;